import { SendOutlined } from '@ant-design/icons';
import { Modal, ModalProps, Select } from 'antd';
import { RuleObject } from 'antd/lib/form';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import useAPIQueries from '../api/useAPIQueries';
import useAPIQuery from '../api/useAPIQuery';
import { JobPostingDetail } from '../types';
import { StageSerializer } from '../types/api';
import EmailForm, { EmailFormValues } from './EmailForm';
import Form from './Form';
import { fixUnorderedListInQuill } from './RichEditor/RichEditor';

function validateRequiredRichEditor(_: RuleObject, value: string) {
  const cleanHtmlRegex = /<[^>]*>/g;

  if (!value.replaceAll(cleanHtmlRegex, '').trim()) {
    //TODO: validator de clean deveria ser usado aqui?
    return Promise.reject(new Error('Campo obrigatório.'));
  }

  return Promise.resolve();
}

export interface SendPracticalTechnicalCaseProps extends ModalProps {
  jobPostingId?: JobPostingDetail['id'];
  stage?: StageSerializer;
  helmetTitle: string;
  loading?: boolean;
  onFinish?: (values: EmailFormValues) => void;
}

const SendPracticalTechnicalCase = ({
  jobPostingId,
  helmetTitle = '',
  loading,
  onFinish,
  stage,
  ...props
}: SendPracticalTechnicalCaseProps) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();

  const interviewScriptTemplate = useAPIQuery(
    [`/interview-script-templates/${stage?.interview_script_template}/`],
    {
      enabled: Boolean(stage?.interview_script_template && !stage.lock_interview_script),
    }
  );

  const interviewScriptTemplateEmailCommunicationTemplate = useAPIQuery(
    `/email-communication-templates/${interviewScriptTemplate.data?.email_communication_template}/`,
    {
      enabled:
        !stage?.email_communication_template &&
        !!interviewScriptTemplate.data?.email_communication_template,
    }
  );

  const stageEmailCommunicationTemplatePracticalTechnicalCase = useAPIQuery(
    `/email-communication-templates/${stage?.email_communication_template}/`,
    {
      enabled: !!stage?.email_communication_template,
    }
  );

  const templateOptions = stageEmailCommunicationTemplatePracticalTechnicalCase.data
    ? [
        {
          key: `${stageEmailCommunicationTemplatePracticalTechnicalCase.data.id}`,
          label: stage?.interview_script_name || 'Personalizado',
          value: stageEmailCommunicationTemplatePracticalTechnicalCase.data.id,
        },
      ]
    : interviewScriptTemplate.data
    ? [
        {
          key: `${interviewScriptTemplate.data.id}`,
          label: interviewScriptTemplate.data?.name,
          value: interviewScriptTemplate.data.id,
        },
      ]
    : [{ key: 'personalized', value: 0, label: 'Personalizado' }];

  const attachmentsStage = useAPIQueries(
    stageEmailCommunicationTemplatePracticalTechnicalCase.data?.attachments.map((attachmentId) => [
      `/attachments/${attachmentId}/`,
      {},
    ]) || []
  );

  const attachmentsInterviewScriptTemplate = useAPIQueries(
    interviewScriptTemplateEmailCommunicationTemplate.data?.attachments.map((attachmentId) => [
      `/attachments/${attachmentId}/`,
      {},
    ]) || []
  );

  const config = useAPIQuery('/configs/tenant-configurations/');

  const user = useAPIQuery('/users/me/');

  return (
    <Modal
      okButtonProps={{ htmlType: 'submit', icon: <SendOutlined />, loading }}
      okText="Enviar"
      onOk={() => form.submit()}
      {...props}
    >
      <Helmet>
        <title>{helmetTitle} | Recrutamento e Seleção</title>
      </Helmet>
      <Form<EmailFormValues>
        form={form}
        layout="vertical"
        initialValues={{
          subject: '',
          message: '',
          attachments: [],
          interview_script_template_email_communication: 0,
          ...(stageEmailCommunicationTemplatePracticalTechnicalCase.data
            ? {
                subject: stageEmailCommunicationTemplatePracticalTechnicalCase.data.subject,
                message: stageEmailCommunicationTemplatePracticalTechnicalCase.data.message,
                attachments:
                  attachmentsStage.data?.map((attachment) => ({
                    uid: attachment.id,
                    name: attachment.filename,
                    status: 'done',
                    url: attachment.file,
                    type: attachment.mimetype,
                    fileName: attachment.filename,
                  })) || [],
                interview_script_template_email_communication:
                  stageEmailCommunicationTemplatePracticalTechnicalCase.data.id,
              }
            : {}),
          ...(!stage?.lock_interview_script && interviewScriptTemplate.data
            ? {
                subject: interviewScriptTemplateEmailCommunicationTemplate.data?.subject,
                message: interviewScriptTemplateEmailCommunicationTemplate.data?.message,
                attachments:
                  attachmentsInterviewScriptTemplate.data?.map((attachment) => ({
                    uid: attachment.id,
                    name: attachment.filename,
                    status: 'done',
                    url: attachment.file,
                    type: attachment.mimetype,
                    fileName: attachment.filename,
                  })) || [],
                interview_script_template_email_communication: interviewScriptTemplate.data.id,
              }
            : {}),
          fromName: config.data?.companyFromName || '',
          enableReplyTo: false,
          email_communication_template: null,
          replyTo: user.data?.email,
        }}
        onFinish={(variables) =>
          onFinish?.({ ...variables, message: fixUnorderedListInQuill(variables.message) })
        }
      >
        <Form.Item
          name="interview_script_template_email_communication"
          label="Roteiro de case prático ou técnico"
        >
          <Select options={templateOptions} disabled />
        </Form.Item>
        <EmailForm
          attachments
          hideTemplateSelection
          image={false}
          replyTo
          fromName
          customVariables={[
            { value: '[candidato]', label: t('Nome do Candidato') },
            { value: '[empresa]', label: t('Nome da Empresa') },
            { value: '[vaga]', label: t('Nome da Vaga') },
          ]}
          richEditorRules={[
            {
              validator: validateRequiredRichEditor,
            },
          ]}
        />
      </Form>
    </Modal>
  );
};

export default SendPracticalTechnicalCase;
